import React, { useState, useRef, useEffect } from "react";
import { ReportItemProps } from "@shared/interfaces/ReportsFiles";
import { useNavigate } from "react-router-dom";
import ConfirmDeleteModal from "@shared/components/ConfirmationModal";
import { useReport } from "@utils/useReports";
import {
  displayReportName,
  formatDate,
  formatTime,
} from "@pages/Project/utils/utils";

const ReportItem: React.FC<ReportItemProps> = ({
  report,
  projectId,
  onDelete,
  sharedToken,
}) => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const { deleteReport } = useReport();

  const handleDropdownClick = (e: React.MouseEvent, reportId: number) => {
    e.stopPropagation();
    setDropdownOpen(dropdownOpen === reportId ? null : reportId);
  };

  const handleReportClick = () => {
    navigate(`/report/${report.id}/project/${projectId}`, {
      state: { sharedToken: sharedToken },
    });
  };

  const handleEditClick = () => {
    navigate(`/report/${report.id}/project/${projectId}`, {
      state: { isEditing: true },
    });
  };

  const handleDelete = async () => {
    try {
      await deleteReport(projectId, `${report.id}`);
      onDelete(report.id.toString());
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting report:", error);
    }
  };

  const createdAtDate = new Date(report.created_at);

  const getPreviewLinesFromContent = (content: string, maxLines = 5) => {
    try {
      const parsed = JSON.parse(content) as { blocks: any[] };
      const blocks = parsed.blocks;

      const lines: string[] = [];

      for (const block of blocks) {
        if (lines.length >= maxLines) break;

        if (block.type === "header" || block.type === "paragraph") {
          lines.push(block.data.text);
        } else if (block.type === "list") {
          for (const item of block.data.items) {
            if (lines.length >= maxLines) break;
            lines.push(`• ${item.content}`);
          }
        }
      }

      return lines.slice(0, maxLines).join("\n");
    } catch (err) {
      console.error("Invalid content format", err);
      return "";
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        cardRef.current &&
        !cardRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div
        className="relative bg-gray-50 border border-gray-300 rounded-lg p-4 pb-3 mb-1 hover:bg-gray-100 hover:shadow transition duration-200 ease-in-out  cursor-pointer flex flex-col justify-between"
        ref={cardRef}
        onClick={handleReportClick}
      >
        <div className="flex justify-between items-start ">
          <div className="flex-grow">
            {report.template_name && (
              <div className="h-[1rem] mb-1 text-xs 2xl:text-sm 2xl:h-[1.5rem] text-ellipsis line-clamp-1 overflow-hidden">
                <p className="text-gray-400">{report.template_name}</p>
              </div>
            )}
            <h3 className="font-bold text-sm 2xl:text-base text-gray-800">
              {displayReportName(
                report.name,
                report.template_name,
                report.created_at
              )}
            </h3>
            <div className="w-[100%]">
              <p className="text-xs 2xl:text-sm texy-500">
                {report.creator?.first_name + " " + report.creator?.last_name} |{" "}
                {`${formatDate(createdAtDate)} - ${formatTime(createdAtDate)}`}
              </p>
              <p className="text-xs 2xl:text-sm my-4 text-gray-800 justify-between line-clamp-4 ">
                {report.content
                  ? getPreviewLinesFromContent(report.content, 4)
                  : ""}
              </p>
            </div>
          </div>
          {!report.published && (
            <div
              className="absolute top-3 right-2"
              ref={dropdownRef}
              onClick={(e) => handleDropdownClick(e, report.id)}
            >
              <button
                type="button"
                className="text-gray-500 focus:outline-none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="2xl:h-5 2xl:w-5 h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="#0357C9"
                >
                  <path d="M10 4a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </button>
              {!isDeleteModalOpen && dropdownOpen && (
                <div className="absolute z-[10] right-0 mt-1 w-40 2xl:w-48 bg-white border border-gray-200 rounded-lg shadow-lg text-sm 2xl:text-base">
                  <button
                    type="button"
                    className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditClick();
                    }}
                  >
                    Edit
                  </button>

                  <button
                    type="button"
                    className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDeleteModalOpen(true);
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between text-gray-500">
          <div className="flex space-x-2 items-center ml-[-0.1rem]">
            {report.public_accessible ? (
              <div className="flex space-x-1 items-center">
                <img
                  src="/lock_open_right.svg"
                  alt="public"
                  className="w-5 2xl:w-[1.35rem]"
                />
                <p className="text-xs 2xl:text-sm mt-1">Public</p>
              </div>
            ) : (
              <div className="flex space-x-1 items-center">
                <img
                  src="/lock.svg"
                  alt="private"
                  className="w-5 2xl:w-[1.35rem]"
                />
                <p className="text-xs 2xl:text-sm mt-1">Private</p>
              </div>
            )}
            <div className="text-gray-500 text-sm mt-1 2xl:text-base flex space-x-1 items-center ">
              <img src="/eye.svg" alt="" className="w-5 2xl:w-6" />
              <span className="font-light">{report.views}</span>
            </div>
          </div>
          {report.published && (
            <div>
              <p className="text-xs 2xl:text-sm text-gray-500 mt-1">
                Published
              </p>
            </div>
          )}
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        entityNames={report.name}
      />
    </>
  );
};

export default ReportItem;
